import { z } from 'zod';
import { OperationIdSchema } from '../../ids';
import {
  ContainerViewSchema,
  InventorySchema,
  ObjectIdSchema,
  TransferDirectionSchema,
} from '../../models/inventory';
import { ItemIdSchema, PositiveQuantitySchema } from '../../models/items';
import { API_PREFIX, defineEndpoint } from '../endpoint';

/** Where a character chose to reappear (HOME technique 10): its lot's hearth or its room. */
export const HomeKindSchema = z.enum(['hearth', 'room']);
export type HomeKind = z.infer<typeof HomeKindSchema>;

/**
 * A character's home (HOME, plan C2): what it chose (null: nothing) and where it would reappear
 * now: its safe room, its lit hearth, or its arrival town; `at` is that place's world tile (its
 * inn's door, its hearth, the arrival), null where the layout has no arrival (the map marks it).
 */
export const HomeViewSchema = z.object({
  chosen: HomeKindSchema.nullable(),
  effective: z.enum(['room', 'hearth', 'arrival']),
  at: z.object({ x: z.number().int(), y: z.number().int() }).nullable(),
});
export type HomeView = z.infer<typeof HomeViewSchema>;

/**
 * Rents a room at an inn for `days` game days (HOME technique 9), standing by its door: paid from
 * the treasury to the power that holds the inn's land (to a sink on free land), its rent added to
 * what is left of it, up to the most days ahead. One room per character. Idempotent.
 */
export const rentRoom = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/inns/:innId/rent`,
  access: 'session',
  params: z.object({ innId: z.string().min(1).max(64) }),
  body: z.object({
    operationId: OperationIdSchema,
    days: z.number().int().positive().max(365),
    /**
     * Its size (HOME TI-3: `lodging.sizes`, a larger chest for a larger rent, the last the cap):
     * absent, the one it has (the first for a new room). A larger one pays the difference for the
     * days already paid; a smaller one gives nothing back and only fits what its chest holds.
     */
    size: z.number().int().min(0).max(4).optional(),
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Moves goods between the backpack and the own room, standing by its inn's door. Nothing comes out
 * of a room whose rent ran out (`room_expired`); goods still go in. Idempotent.
 */
export const transferRoomItems = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/room/items`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/** Moves one unique object between the backpack and the own room, like goods. Idempotent. */
export const transferRoomObject = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/room/objects`,
  access: 'session',
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    object: ObjectIdSchema,
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/** The character's home: what it chose and where it would reappear now. */
export const getHome = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/me/home`,
  access: 'session',
  response: HomeViewSchema,
});

/**
 * Chooses where to reappear (HOME technique 10), standing there (R-60): by the own lot's lit
 * hearth, or by the inn of the own room, paid and safe.
 */
export const setHome = defineEndpoint({
  method: 'PUT',
  path: `${API_PREFIX}/me/home`,
  access: 'session',
  body: z.object({ kind: HomeKindSchema }),
  response: HomeViewSchema,
});

const InnParamsSchema = z.object({ innId: z.string().min(1).max(64) });
const InnRoomParamsSchema = InnParamsSchema.extend({ number: z.coerce.number().int().positive() });

/**
 * A room of an inn as whoever stands by its door sees it (AR-2): its number, never whose; a forced
 * one shows what lies in it (AR-3: open to anyone there).
 */
export const InnRoomSchema = z.object({
  number: z.number().int().positive(),
  forced: z.boolean(),
  contents: ContainerViewSchema.nullable(),
});
export type InnRoom = z.infer<typeof InnRoomSchema>;

/**
 * The rooms taken at an inn (HOME, RV-14, AR-2), standing by its door: by number, the own one
 * named. A sanctuary's inn lists none: its rooms are never forced. A fallen town's stand open.
 */
export const getInnRooms = defineEndpoint({
  method: 'GET',
  path: `${API_PREFIX}/inns/:innId/rooms`,
  access: 'session',
  params: InnParamsSchema,
  response: z.object({
    safe: z.boolean(),
    /** Its town fell (RV-13, IF-1): every room stands open until it stands again. */
    fallen: z.boolean(),
    own: z.number().int().positive().nullable(),
    rooms: z.array(InnRoomSchema),
  }),
});

/**
 * Moves goods between the backpack and someone else's forced room (AR-3), standing by its inn's
 * door: taking is a theft by rule. Idempotent.
 */
export const transferInnRoomItems = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/inns/:innId/rooms/:number/items`,
  access: 'session',
  params: InnRoomParamsSchema,
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    item: ItemIdSchema,
    quantity: PositiveQuantitySchema,
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/** Moves one object between the backpack and someone else's forced room, like goods. */
export const transferInnRoomObject = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/inns/:innId/rooms/:number/objects`,
  access: 'session',
  params: InnRoomParamsSchema,
  body: z.object({
    operationId: OperationIdSchema,
    direction: TransferDirectionSchema,
    object: ObjectIdSchema,
  }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});

/**
 * Locks the own forced room again (AR-3), standing by its inn's door: free. Any move of the owner in
 * it locks it too. Idempotent.
 */
export const relockRoom = defineEndpoint({
  method: 'POST',
  path: `${API_PREFIX}/me/room/relock`,
  access: 'session',
  body: z.object({ operationId: OperationIdSchema }),
  response: z.object({ operationId: OperationIdSchema, inventory: InventorySchema }),
});
